import React, { useContext } from 'react';
import { map } from 'lodash';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { SlideListItem } from './SlideListItem';
import { SlideThumbnail } from './SlideThumbnail';
import { SlideActionBar } from './SlideActionBar';
import { CourseEditorContext, COURSE_MODES } from '../../../../contexts/CourseEditorContext';
import { UserReactContext } from '../../../../contexts/UserContext';
import './SlideList.scss';

/**
 * SlideList renders a sortable, selectable list of slide thumbnails.
 * Gets mode, completedSections, and isSectionAccessible from CourseEditorContext.
 * Gets isSuperAdmin status from UserReactContext.
 *
 * @param {object} params - Component parameters container
 * @param {Array<object>} params.items - Items to render (generic objects)
 * @param {function(object): string} params.getItemId - Returns unique id for an item
 * @param {string} params.selectedItemId - Currently selected item id
 * @param {function({id: string, item: object}): void} params.onSelect - Callback when an item is selected
 * @param {boolean} params.isEditMode - Whether sorting is enabled (drag handles)
 * @param {function(): void} [params.onAddItem] - Optional handler to add an item (shows Add button when provided and edit mode is true)
 * @param {function(string, string): void} [params.onEditItemTitle] - Handler to edit item title
 * @param {function(string): void} [params.onDuplicateItem] - Handler to duplicate an item
 * @param {function(string): void} [params.onRemoveItem] - Handler to remove an item
 */
export const SlideList = ({
    items,
    getItemId,
    selectedItemId,
    onSelect,
    onAddItem,
    onEditItemTitle,
    onDuplicateItem,
    onRemoveItem
}) => {
    // Get values from contexts instead of props
    // completedSections is now an object: { sectionId: { selectedOptionIds: [...], answeredAt: '...' } }
    const {
        mode,
        completedSections = {},
        isSectionAccessible
    } = useContext(CourseEditorContext) || {};

    const userContext = useContext(UserReactContext);
    const isSuperAdminMode = userContext?.isUserSuperAdmin() || false;
    const isEditMode = mode === COURSE_MODES.EDIT;

    const itemIds = map(items, (it) => getItemId(it));

    return (
        <div className="SlideList">
            <div className="slideListContent">
                <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                    {map(items, (item) => {
                        const id = getItemId(item);
                        // Check if section is completed using object structure
                        const isCompleted = completedSections[id] ? true : false;
                        const isAccessible = isSectionAccessible ? isSectionAccessible(id) : true;

                        return (
                            <React.Fragment key={id}>
                                <SlideListItem
                                    id={id}
                                    isSelected={id === selectedItemId}
                                    isEditMode={isEditMode}
                                    onClick={() => onSelect && onSelect({ id, item })}
                                    isCompleted={isCompleted}
                                    isAccessible={isAccessible}
                                    mode={mode}
                                >
                                    <div className="slideListItemInner">
                                        <div className="slideListItemHeader">
                                            {isEditMode && onEditItemTitle ? (
                                                <input
                                                    className="slideListItemTitleInput"
                                                    defaultValue={item.title || ''}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => onEditItemTitle(id, e.target.value)}
                                                />
                                            ) : (
                                                <div className="slideListItemTitle" title={item.title || ''}>
                                                    {item.title || ''}
                                                </div>
                                            )}
                                        </div>
                                        <SlideThumbnail item={item} isSuperAdminMode={isSuperAdminMode} />
                                    </div>
                                </SlideListItem>
                                {isEditMode && id === selectedItemId && (
                                    <div className="slideActionBarWrapper" onClick={(e) => e.stopPropagation()}>
                                        <SlideActionBar
                                            onAddAfter={() => onAddItem && onAddItem(id)}
                                            onDuplicate={() => onDuplicateItem && onDuplicateItem(id)}
                                            onRemove={() => onRemoveItem && onRemoveItem(id)}
                                        />
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </SortableContext>
            </div>
        </div>
    );
};


